import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Box, Flex, HStack, VStack } from "@chakra-ui/react";
import { faBox } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "antd";
import { CustomerRoutes } from "appRoutePaths";
import useZendeskSSO from "hooks/useZendeskSSO";
import { SupportTicket, SupportTicketStatuses } from "models/support";
import { FC } from "react";
import { Link } from "react-router-dom";
import { ZENDESK_BASE_URL } from "util/config";
import { DefaultPageRequest } from "util/getDefaultPageRequest";
import { pageRequestToUrlSearchParams } from "util/helpers";

const packagesProcessingFilter = (packageID: string) => {
    return pageRequestToUrlSearchParams({
        ...DefaultPageRequest,
        filterBy: {
            friendlyId: [packageID],
        },
    });
};
const packagesProcessingLink = (packageID: string) =>
    `${CustomerRoutes.packages.url}?${packagesProcessingFilter(packageID)}`;

const TICKET_TAG_NOTIFICATION = "customer_notification";

const STATUS_COLORS: Record<SupportTicketStatuses, string> = {
    open: "lightBlue",
    solved: "darkGreen",
    new: "red",
    pending: "orange",
    closed: "red",
    hold: "orange",
};

const packageIDRegex = new RegExp(/Package ID (\d+) Received/);

const TicketHeader: FC<{
    ticket: SupportTicket;
    isExpanded: boolean;
    unread: boolean;
}> = ({ ticket, isExpanded, unread }) => {
    const {
        id,
        subject,
        description,
        status,
        tags,
        updatedAt: updatedAtStr,
    } = ticket;

    const isNotification = tags.includes(TICKET_TAG_NOTIFICATION);
    const hasPackageID = isNotification && subject.indexOf("Package ID") > 1;

    const packageIDMatches = subject?.match(packageIDRegex);
    const packageID =
        packageIDMatches && packageIDMatches?.length > 0
            ? packageIDMatches[1]
            : undefined;
    const updatedAt = new Date(updatedAtStr);
    const updatedAtDate = updatedAt.toLocaleDateString();
    const updatedAtTime = updatedAt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    });

    const makeZendeskLink = useZendeskSSO();

    const openZendeskComment = async () => {
        const commentUrl = `${ZENDESK_BASE_URL}/hc/en-us/requests/${ticket.id}`;
        const zendeskLink = await makeZendeskLink(commentUrl);
        window.open(zendeskLink, "_blank");
    };

    return (
        <VStack w="100%">
            <HStack
                w="100%"
                color="text"
                alignItems="baseline"
                fontWeight="medium"
            >
                <Box
                    textTransform="capitalize"
                    minWidth="3rem"
                    color={STATUS_COLORS[status]}
                    fontWeight="semibold"
                    fontSize={14}
                >
                    {unread && (
                        <Badge
                            dot
                            status="processing"
                            color="red"
                            style={{ marginRight: "4px" }}
                        />
                    )}
                    {hasPackageID && packageID && (
                        <Link
                            to={packagesProcessingLink(packageID)}
                            style={{
                                marginLeft: unread ? "14px" : "24px",
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faBox}
                                color="var(--dcs-blue)"
                            />
                        </Link>
                    )}
                </Box>
                <Box flex="1">
                    <Box
                        noOfLines={1}
                        color={unread ? "white" : "lightgray"}
                        fontWeight={unread ? "bold" : ""}
                    >
                        {subject}{" "}
                        <Box display={{ base: "none", md: "" }}>
                            - {description}
                        </Box>
                    </Box>
                    {isExpanded && (
                        <HStack>
                            <Box
                                color="white"
                                px={2}
                                fontWeight="bold"
                                background="linear-gradient(90deg,rgba(24, 144, 255, 1) 0%, rgba(0, 53, 116, 1) 100%)"
                                rounded="full"
                                justifySelf="start"
                                fontSize={14}
                                marginTop={1}
                            >
                                #<Box as="span">{id}</Box>
                            </Box>
                            <Box>
                                <ExternalLinkIcon
                                    fontSize={22}
                                    color="lightBlue"
                                    onClick={openZendeskComment}
                                    cursor="pointer"
                                    _hover={{ filter: "brightness(1.2)" }}
                                />
                            </Box>
                        </HStack>
                    )}
                </Box>

                <Flex
                    minWidth="10rem"
                    alignItems="end"
                    direction="column"
                    pr={2}
                >
                    <Box>{updatedAtDate}</Box>
                    {isExpanded && (
                        <Box opacity={0.5} fontSize="0.8em">
                            {updatedAtTime}
                        </Box>
                    )}
                </Flex>
            </HStack>
        </VStack>
    );
};

export default TicketHeader;
